import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { calendarService } from '../services/calendarService';
import { bookingService } from '../services/bookingService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiMessageSquare, FiCheck } = FiIcons;

const BookingPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [calendar, setCalendar] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  useEffect(() => {
    loadCalendar();
  }, [shareId]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const calendarData = await calendarService.getCalendarByShareId(shareId);
      setCalendar(calendarData);
    } catch (error) {
      toast.error('Kalender nicht gefunden');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
    setShowBookingModal(true);
  };

  const onSubmit = async (data) => {
    if (!selectedSlot) return;

    setBooking(true);
    try {
      const bookingData = await bookingService.createBooking(shareId, selectedSlot.id, data);
      toast.success('Termin erfolgreich gebucht!');
      navigate(`/booking/confirmation/${bookingData.id}`);
    } catch (error) {
      toast.error(error.message || 'Fehler bei der Buchung');
    } finally {
      setBooking(false);
    }
  };

  const closeModal = () => {
    setShowBookingModal(false);
    setSelectedSlot(null);
    reset();
  };

  const groupSlotsByDate = (slots) => {
    const grouped = {};
    slots.forEach(slot => {
      const date = slot.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!calendar) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Kalender nicht gefunden
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Der angeforderte Kalender existiert nicht oder ist nicht verfügbar.
          </p>
        </Card>
      </div>
    );
  }

  const availableSlots = calendar.slots?.filter(slot => slot.isAvailable) || [];
  const groupedSlots = groupSlotsByDate(availableSlots);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {calendar.title}
          </h1>
          {calendar.description && (
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {calendar.description}
            </p>
          )}
        </motion.div>

        {/* Available Slots */}
        {availableSlots.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-8 text-center">
              <SafeIcon icon={FiCalendar} className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Keine Termine verfügbar
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Aktuell sind keine Termine zur Buchung verfügbar. Bitte versuchen Sie es später erneut.
              </p>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedSlots).map(([date, slots], dateIndex) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: dateIndex * 0.1 }}
              >
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                    {format(parseISO(date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                  </h2>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {slots.map((slot, slotIndex) => (
                      <motion.button
                        key={slot.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: (dateIndex * 0.1) + (slotIndex * 0.05) }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSlotSelection(slot)}
                        className="p-4 border-2 border-primary-200 dark:border-primary-800 rounded-lg hover:border-primary-400 dark:hover:border-primary-600 transition-colors bg-white dark:bg-gray-800 text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center text-primary-600 dark:text-primary-400 font-medium">
                              <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                              {slot.time}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {slot.duration} Minuten
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-success-100 dark:bg-success-900 flex items-center justify-center">
                            <SafeIcon icon={FiCheck} className="w-4 h-4 text-success-600 dark:text-success-400" />
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Booking Modal */}
        <Modal
          isOpen={showBookingModal}
          onClose={closeModal}
          title="Termin buchen"
          size="md"
        >
          {selectedSlot && (
            <div>
              {/* Selected Slot Info */}
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-primary-900 dark:text-primary-100 mb-2">
                  Gewählter Termin
                </h3>
                <div className="text-sm text-primary-700 dark:text-primary-300">
                  <div className="flex items-center mb-1">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                    {format(parseISO(selectedSlot.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                    {selectedSlot.time} ({selectedSlot.duration} Min.)
                  </div>
                </div>
              </div>

              {/* Booking Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                        errors.name 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="Ihr vollständiger Name"
                      {...register('name', { required: 'Name ist erforderlich' })}
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    E-Mail *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                        errors.email 
                          ? 'border-red-300 dark:border-red-600' 
                          : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      placeholder="ihre@email.de"
                      {...register('email', {
                        required: 'E-Mail ist erforderlich',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Ungültige E-Mail-Adresse'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Telefon (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiPhone} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="+49 123 456789"
                      {...register('phone')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nachricht (optional)
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <SafeIcon icon={FiMessageSquare} className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      rows={3}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Zusätzliche Informationen..."
                      {...register('notes')}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Abbrechen
                  </Button>
                  <Button type="submit" loading={booking}>
                    Termin buchen
                  </Button>
                </div>
              </form>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default BookingPage;